import { JsonApiRequestError } from '@farfetched/core';
import { combine, Store } from 'effector';
import {
	AccountReportRegistryFilters,
	AccountReportWithId,
	createAccountReportRegistryQuery,
} from 'shared/api';
import {
	createDateFilterModel,
	createRegistryModel,
	DateFilterModel,
	getToday,
	RegistryCallbacks,
	RegistryModel,
} from 'shared/lib';

export type AccountReportRegistryTotal = {
	deposits: number;
	withdrawals: number;
	jackpots: number;
	bonuses: number;
	income: number;
	rtp: number;
	numberOfBets: number;
	currency: string;
	incomeAllSources: number;
	rtpAllSources: number;
};

export type AccountReportRegistryModel = {
	$total: Store<AccountReportRegistryTotal | null>;
	$isMultiCurrencies: Store<boolean>;
	model: RegistryModel<AccountReportWithId, AccountReportRegistryFilters, JsonApiRequestError>;
	dateFilterModel: DateFilterModel;
};

export type CreateAccountReportRegistryModelParams = RegistryCallbacks;

export const createAccountReportRegistryModel = (
	params: CreateAccountReportRegistryModelParams,
): AccountReportRegistryModel => {
	const query = createAccountReportRegistryQuery();

	const [todayDate, currentTimeZone] = getToday();

	const model = createRegistryModel({
		query,
		filters: {
			currencies: [],
			accountIds: [],
			dateTo: todayDate,
			dateFrom: todayDate,
			timeZone: currentTimeZone,
		},
		sortField: 'name',
		...params,
	});

	const dateFilterModel = createDateFilterModel({
		fromChange: model.filters.dateFrom.setValue,
		toChange: model.filters.dateTo.setValue,
	});

	const $isMultiCurrencies = query.$data.map((data) => {
		const currencies = new Set<string>(data?.rows.map((row) => row.currency) ?? []);

		return currencies.size > 1;
	});

	const $total = combine(
		{
			data: query.$data,
			isMultiCurrencies: $isMultiCurrencies,
		},
		({ data, isMultiCurrencies }) => {
			if (!data) return null;

			const total = data.rows.reduce(
				(acc, row) => ({
					deposits: acc.deposits + row.deposits,
					withdrawals: acc.withdrawals + row.withdrawals,
					income: acc.income + row.income,
					rtp: acc.rtp + row.rtp,
					jackpots: acc.jackpots + row.jackpots,
					bonuses: acc.bonuses + row.bonuses,
					numberOfBets: acc.numberOfBets + row.numberOfBets,
					incomeAllSources: acc.incomeAllSources + row.incomeAllSources,
					rtpAllSources: acc.rtpAllSources + row.rtpAllSources,
				}),
				{
					deposits: 0,
					withdrawals: 0,
					jackpots: 0,
					bonuses: 0,
					income: 0,
					rtp: 0,
					numberOfBets: 0,
					incomeAllSources: 0,
					rtpAllSources: 0,
				},
			);

			return {
				...total,
				income: Number(total.income.toFixed(2)),
				withdrawals: Number(total.withdrawals.toFixed(2)),
				currency: isMultiCurrencies ? '' : data.rows[0].currency,
				rtp: total.deposits && Number(((1 - total.income / total.deposits) * 100).toFixed(2)),
				rtpAllSources:
					total.deposits &&
					Number(((1 - total.incomeAllSources / total.deposits) * 100).toFixed(2)),
			};
		},
	);

	return { $total, $isMultiCurrencies, model, dateFilterModel };
};
